// BillingEntities.js — staff editor for a multi-billing client's billing
// entities (default Zoho contact + extra client_billing_profiles).
// Rendered inside EditClient when the Multi-billing toggle is ON.
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Select from 'react-select';
import './billingEntities.css';

const STATE_OPTIONS = [
  'Dubai',
  'Abu Dhabi',
  'Sharjah',
  'Ajman',
  'Ras Al Khaimah',
  'Fujairah',
  'Umm Al Quwain',
];

// Inline EDIT keeps label + single legal_name (relabelled per entity type).
const EMPTY_ENTITY_FORM = {
  label: '',
  legal_name: '',
  entity_type: 'individual',
  trn_number: '',
  is_vat_registered: false,
  address_line_1: '',
  city: '',
  state: 'Dubai',
  country: 'United Arab Emirates',
};

// CREATE is name-driven: Individual (the common case — personal unit owners)
// takes first + last name; Business takes the business name. Label is gone —
// the backend auto-derives it from the name.
const EMPTY_CREATE_FORM = {
  entity_type: 'individual',
  first_name: '',
  last_name: '',
  legal_name: '',
  trn_number: '',
  is_vat_registered: false,
  address_line_1: '',
  city: '',
  state: 'Dubai',
  country: 'United Arab Emirates',
};

function entityToForm(profile) {
  return {
    label: profile.label || '',
    legal_name: profile.legal_name || '',
    first_name: profile.first_name || '',
    last_name: profile.last_name || '',
    entity_type: profile.entity_type === 'business' ? 'business' : 'individual',
    trn_number: profile.trn_number || '',
    is_vat_registered: !!profile.is_vat_registered,
    address_line_1: profile.address_line_1 || '',
    city: profile.city || '',
    state: profile.state || 'Dubai',
    country: profile.country || 'United Arab Emirates',
  };
}

function BillingEntities({ clientId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');

  // 'link' | 'create' | null — which add panel is open
  const [addMode, setAddMode] = useState(null);
  // Link panel: searchable dropdown of genuinely-linkable Zoho customers
  // (server already excludes active clients' contacts + linked profiles).
  const [linkOptions, setLinkOptions] = useState([]);
  const [linkOptionsLoading, setLinkOptionsLoading] = useState(false);
  const [selectedZohoCustomer, setSelectedZohoCustomer] = useState(null);
  const [createForm, setCreateForm] = useState({ ...EMPTY_CREATE_FORM });
  const [addError, setAddError] = useState('');
  const [saving, setSaving] = useState(false);

  // Inline edit of one profile
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ ...EMPTY_ENTITY_FORM });
  const [editError, setEditError] = useState('');

  const fetchProfiles = useCallback(async () => {
    setLoading(true);
    setListError('');
    try {
      const res = await axios.get(`/api/clients/${clientId}/billing-profiles`);
      setData(res.data);
    } catch (err) {
      console.error('Error fetching billing profiles:', err);
      setListError(err.response?.data?.error || 'Failed to load billing entities.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  const fetchLinkOptions = useCallback(async () => {
    setLinkOptionsLoading(true);
    try {
      const res = await axios.get(
        `/api/clients/${clientId}/billing-profiles/available-zoho-customers`
      );
      const customers = res.data?.customers || [];
      setLinkOptions(customers.map((c) => ({
        value: c.contact_id,
        label: c.company && c.company !== c.name ? `${c.name} — ${c.company}` : c.name,
      })));
    } catch (err) {
      console.error('Error fetching linkable Zoho customers:', err);
      setAddError(err.response?.data?.error || 'Failed to load Zoho customers.');
    } finally {
      setLinkOptionsLoading(false);
    }
  }, [clientId]);

  const openAddPanel = (mode) => {
    setAddError('');
    setEditingId(null);
    setEditError('');
    if (addMode === mode) {
      setAddMode(null);
      return;
    }
    setSelectedZohoCustomer(null);
    setCreateForm({ ...EMPTY_CREATE_FORM });
    setAddMode(mode);
    // Org-wide list, server-cached 5 min — fetch fresh each time the panel
    // opens; react-select's own search filters it client-side.
    if (mode === 'link') fetchLinkOptions();
  };

  const handleLinkSubmit = async () => {
    if (!selectedZohoCustomer) {
      setAddError('Pick a Zoho customer to link.');
      return;
    }
    setSaving(true);
    setAddError('');
    try {
      // Label auto-derives server-side from the customer's name.
      await axios.post(`/api/clients/${clientId}/billing-profiles`, {
        mode: 'link',
        zoho_contact_id: selectedZohoCustomer.value,
      });
      setAddMode(null);
      await fetchProfiles();
    } catch (err) {
      console.error('Error linking billing entity:', err);
      // 400/409 (e.g. someone else linked it meanwhile) shown verbatim.
      setAddError(err.response?.data?.error || 'Failed to link the Zoho customer.');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateSubmit = async () => {
    const isIndividual = createForm.entity_type !== 'business';
    if (isIndividual) {
      if (!(createForm.first_name || '').trim() || !(createForm.last_name || '').trim()) {
        setAddError('First name and last name are required for an individual.');
        return;
      }
    } else if (!(createForm.legal_name || '').trim()) {
      setAddError('Business name is required.');
      return;
    }

    setSaving(true);
    setAddError('');
    try {
      // Label is auto-derived server-side (first+last / business name).
      const body = {
        mode: 'create',
        entity_type: createForm.entity_type,
        trn_number: createForm.is_vat_registered ? (createForm.trn_number || '').trim() : '',
        is_vat_registered: !!createForm.is_vat_registered,
        address_line_1: (createForm.address_line_1 || '').trim(),
        city: (createForm.city || '').trim(),
        state: createForm.state,
        country: createForm.country,
      };
      if (isIndividual) {
        body.first_name = createForm.first_name.trim();
        body.last_name = createForm.last_name.trim();
      } else {
        body.legal_name = createForm.legal_name.trim();
        // Optional contact person for the business's Zoho customer.
        if ((createForm.first_name || '').trim() && (createForm.last_name || '').trim()) {
          body.first_name = createForm.first_name.trim();
          body.last_name = createForm.last_name.trim();
        }
      }
      await axios.post(`/api/clients/${clientId}/billing-profiles`, body);
      setAddMode(null);
      await fetchProfiles();
    } catch (err) {
      console.error('Error creating billing entity:', err);
      setAddError(err.response?.data?.error || 'Failed to create the billing entity.');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (profile) => {
    setAddMode(null);
    setAddError('');
    setEditError('');
    if (editingId === profile.id) {
      setEditingId(null);
      return;
    }
    setEditingId(profile.id);
    setEditForm(entityToForm(profile));
  };

  const handleEditSubmit = async (profileId) => {
    const isIndividual = editForm.entity_type !== 'business';
    if (isIndividual) {
      if (!(editForm.first_name || '').trim() || !(editForm.last_name || '').trim()) {
        setEditError('First name and last name are required for an individual.');
        return;
      }
    } else if (!(editForm.legal_name || '').trim()) {
      setEditError('Business name is required.');
      return;
    }
    setSaving(true);
    setEditError('');
    try {
      await axios.put(`/api/clients/${clientId}/billing-profiles/${profileId}`, {
        label: (editForm.label || '').trim() || undefined,
        // Individual legal_name is re-derived server-side from first+last.
        legal_name: (editForm.legal_name || '').trim(),
        first_name: (editForm.first_name || '').trim(),
        last_name: (editForm.last_name || '').trim(),
        entity_type: editForm.entity_type,
        trn_number: (editForm.trn_number || '').trim(),
        is_vat_registered: !!editForm.is_vat_registered,
        address_line_1: (editForm.address_line_1 || '').trim(),
        city: (editForm.city || '').trim(),
        state: editForm.state,
        country: editForm.country,
      });
      setEditingId(null);
      await fetchProfiles();
    } catch (err) {
      console.error('Error updating billing entity:', err);
      setEditError(err.response?.data?.error || 'Failed to update the billing entity.');
    } finally {
      setSaving(false);
    }
  };

  // Separate wallet per entity (PLAN_entity_wallets.md, owner 2026-09-23):
  // its own balance, top-ups and history; the entity's properties charge it
  // instead of the main wallet. Default off; managers only; OFF is refused by
  // the server while the wallet still holds money.
  const isManager = localStorage.getItem('userRole') === 'manager';
  const [walletBusyId, setWalletBusyId] = useState(null);
  const toggleWallet = async (profile) => {
    const turningOn = !profile.wallet_client_id;
    const ok = window.confirm(turningOn
      ? `Give "${profile.label}" its own wallet?\n\nBookings on this entity's properties will be charged from that wallet, and its top-ups and receipts stay on the entity's Zoho contact. The main wallet is not touched.`
      : `Switch "${profile.label}" back to the shared wallet?\n\nThis only works while its wallet is empty.`);
    if (!ok) return;
    setWalletBusyId(profile.id);
    try {
      await axios.put(`/api/clients/${clientId}/billing-profiles/${profile.id}/separate-wallet`, { on: turningOn });
      await fetchProfiles();
    } catch (e) {
      window.alert(e.response?.data?.error || 'Could not change the wallet setting.');
    } finally {
      setWalletBusyId(null);
    }
  };

  const handleDelete = async (profile) => {
    const ok = window.confirm(
      `Delete billing entity "${profile.label || profile.legal_name}"? ` +
      'Properties billed to it will revert to the default entity.'
    );
    if (!ok) return;

    setSaving(true);
    setListError('');
    try {
      await axios.delete(`/api/clients/${clientId}/billing-profiles/${profile.id}`);
      if (editingId === profile.id) setEditingId(null);
      await fetchProfiles();
    } catch (err) {
      console.error('Error deleting billing entity:', err);
      setListError(err.response?.data?.error || 'Failed to delete the billing entity.');
    } finally {
      setSaving(false);
    }
  };

  // EDIT-ONLY fields — the backend stores a single legal_name, so the inline
  // edit keeps one name field, relabelled per entity type. Create (below) is
  // the richer first/last flow.
  const renderEntityFields = (form, setForm) => (
    <div className="billing-entities__form-grid">
      <div className="billing-entities__field">
        <label className="billing-entities__label">Label</label>
        <input
          className="billing-entities__input"
          type="text"
          value={form.label}
          onChange={(e) => setForm((p) => ({ ...p, label: e.target.value }))}
          placeholder="Short name shown on invoice lists"
        />
      </div>

      {form.entity_type === 'business' && (
        <div className="billing-entities__field">
          <label className="billing-entities__label">Business Name *</label>
          <input
            className="billing-entities__input"
            type="text"
            value={form.legal_name}
            onChange={(e) => setForm((p) => ({ ...p, legal_name: e.target.value }))}
            placeholder="Registered company name"
          />
        </div>
      )}

      <div className="billing-entities__field">
        <label className="billing-entities__label">
          {form.entity_type === 'business' ? 'Contact First Name' : 'First Name *'}
        </label>
        <input
          className="billing-entities__input"
          type="text"
          value={form.first_name}
          onChange={(e) => setForm((p) => ({ ...p, first_name: e.target.value }))}
          placeholder="e.g. Sarah"
        />
      </div>

      <div className="billing-entities__field">
        <label className="billing-entities__label">
          {form.entity_type === 'business' ? 'Contact Last Name' : 'Last Name *'}
        </label>
        <input
          className="billing-entities__input"
          type="text"
          value={form.last_name}
          onChange={(e) => setForm((p) => ({ ...p, last_name: e.target.value }))}
          placeholder="e.g. Al Marri"
        />
      </div>

      <div className="billing-entities__field">
        <label className="billing-entities__label">Entity Type</label>
        <select
          className="billing-entities__input"
          value={form.entity_type}
          onChange={(e) => setForm((p) => ({ ...p, entity_type: e.target.value }))}
        >
          <option value="individual">Individual</option>
          <option value="business">Business</option>
        </select>
      </div>

      <div className="billing-entities__field">
        <label className="billing-entities__label">VAT Registered</label>
        <label className="billing-entities__checkbox">
          <input
            type="checkbox"
            checked={!!form.is_vat_registered}
            onChange={(e) => setForm((p) => ({ ...p, is_vat_registered: e.target.checked }))}
          />
          <span>{form.is_vat_registered ? 'Yes' : 'No'}</span>
        </label>
      </div>

      {form.is_vat_registered && (
        <div className="billing-entities__field">
          <label className="billing-entities__label">TRN Number</label>
          <input
            className="billing-entities__input"
            type="text"
            value={form.trn_number}
            onChange={(e) => setForm((p) => ({ ...p, trn_number: e.target.value }))}
          />
        </div>
      )}

      <div className="billing-entities__field">
        <label className="billing-entities__label">Address Line 1</label>
        <input
          className="billing-entities__input"
          type="text"
          value={form.address_line_1}
          onChange={(e) => setForm((p) => ({ ...p, address_line_1: e.target.value }))}
          placeholder="Office / Address"
        />
      </div>

      <div className="billing-entities__field">
        <label className="billing-entities__label">City</label>
        <input
          className="billing-entities__input"
          type="text"
          value={form.city}
          onChange={(e) => setForm((p) => ({ ...p, city: e.target.value }))}
        />
      </div>

      <div className="billing-entities__field">
        <label className="billing-entities__label">State/Emirate</label>
        <select
          className="billing-entities__input"
          value={form.state}
          onChange={(e) => setForm((p) => ({ ...p, state: e.target.value }))}
        >
          {STATE_OPTIONS.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      <div className="billing-entities__field">
        <label className="billing-entities__label">Country</label>
        <select
          className="billing-entities__input"
          value={form.country}
          onChange={(e) => setForm((p) => ({ ...p, country: e.target.value }))}
        >
          <option value="United Arab Emirates">United Arab Emirates</option>
        </select>
      </div>
    </div>
  );

  const profiles = data?.profiles || [];

  return (
    <div className="billing-entities">
      {loading ? (
        <div className="billing-entities__muted">Loading billing entities...</div>
      ) : (
        <>
          {listError && <div className="billing-entities__error">{listError}</div>}

          <div className="billing-entities__list">
            {/* Default entity — the client's own Zoho contact */}
            {data?.default_entity && (
              <div className="billing-entities__row billing-entities__row--default">
                <div className="billing-entities__row-main">
                  <div className="billing-entities__row-title">
                    {data.default_entity.label || 'Default entity'}
                    <span className="billing-entities__badge">Default</span>
                  </div>
                  <div className="billing-entities__row-sub">
                    Zoho contact: {data.default_entity.zoho_contact_id || '—'} · Managed in the client fields above
                  </div>
                </div>
              </div>
            )}

            {profiles.map((profile) => (
              <div className="billing-entities__row" key={profile.id}>
                <div className="billing-entities__row-main">
                  <div className="billing-entities__row-title">{profile.label || profile.legal_name}</div>
                  <div className="billing-entities__row-sub">
                    {profile.legal_name}
                    {profile.trn_number ? ` · TRN ${profile.trn_number}` : ''}
                    {` · ${profile.property_count || 0} propert${(profile.property_count || 0) === 1 ? 'y' : 'ies'}`}
                    {profile.wallet_client_id ? ' · own wallet' : ''}
                  </div>
                  <label className={`billing-entities__wallet${profile.wallet_client_id ? ' is-on' : ''}`} title="Separate wallet: this entity's bookings are charged from its own wallet and its deposits stay on its own Zoho contact.">
                    <input
                      type="checkbox"
                      checked={!!profile.wallet_client_id}
                      onChange={() => toggleWallet(profile)}
                      disabled={!isManager || walletBusyId === profile.id || saving}
                    />
                    <span>{walletBusyId === profile.id ? 'Saving…' : 'Separate wallet'}</span>
                  </label>
                </div>

                <div className="billing-entities__row-actions">
                  <button
                    type="button"
                    className="billing-entities__btn billing-entities__btn--ghost"
                    onClick={() => startEdit(profile)}
                    disabled={saving}
                  >
                    {editingId === profile.id ? 'Close' : 'Edit'}
                  </button>
                  <button
                    type="button"
                    className="billing-entities__btn billing-entities__btn--danger"
                    onClick={() => handleDelete(profile)}
                    disabled={saving}
                  >
                    Delete
                  </button>
                </div>

                {editingId === profile.id && (
                  <div className="billing-entities__panel">
                    {renderEntityFields(editForm, setEditForm)}
                    {editError && <div className="billing-entities__error">{editError}</div>}
                    <div className="billing-entities__panel-actions">
                      <button
                        type="button"
                        className="billing-entities__btn billing-entities__btn--ghost"
                        onClick={() => { setEditingId(null); setEditError(''); }}
                        disabled={saving}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="billing-entities__btn billing-entities__btn--primary"
                        onClick={() => handleEditSubmit(profile.id)}
                        disabled={saving}
                      >
                        {saving ? 'Saving...' : 'Save Entity'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}

            {profiles.length === 0 && (
              <div className="billing-entities__muted">
                No extra billing entities yet.
              </div>
            )}
          </div>

          <div className="billing-entities__add-actions">
            <button
              type="button"
              className="billing-entities__btn billing-entities__btn--ghost"
              onClick={() => openAddPanel('link')}
              disabled={saving}
            >
              Link existing Zoho customer
            </button>
            <button
              type="button"
              className="billing-entities__btn billing-entities__btn--primary"
              onClick={() => openAddPanel('create')}
              disabled={saving}
            >
              Add new entity
            </button>
          </div>

          {addMode === 'link' && (
            <div className="billing-entities__panel">
              <div className="billing-entities__panel-title">Link existing Zoho customer</div>
              <div className="billing-entities__panel-sub">
                Only customers not already attached to an active client or billing entity are listed.
              </div>
              <div className="billing-entities__field">
                <label className="billing-entities__label">Zoho Customer *</label>
                <Select
                  classNamePrefix="hhc-select"
                  options={linkOptions}
                  value={selectedZohoCustomer}
                  onChange={(option) => { setSelectedZohoCustomer(option); setAddError(''); }}
                  isLoading={linkOptionsLoading}
                  isSearchable
                  isClearable
                  placeholder={linkOptionsLoading ? 'Loading Zoho customers...' : 'Search by name or company'}
                  noOptionsMessage={() => (linkOptionsLoading ? 'Loading...' : 'No linkable Zoho customers found')}
                />
              </div>
              {addError && <div className="billing-entities__error">{addError}</div>}
              <div className="billing-entities__panel-actions">
                <button
                  type="button"
                  className="billing-entities__btn billing-entities__btn--ghost"
                  onClick={() => setAddMode(null)}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="billing-entities__btn billing-entities__btn--primary"
                  onClick={handleLinkSubmit}
                  disabled={saving}
                >
                  {saving ? 'Linking...' : 'Link Customer'}
                </button>
              </div>
            </div>
          )}

          {addMode === 'create' && (
            <div className="billing-entities__panel">
              <div className="billing-entities__panel-title">Add new entity</div>
              <div className="billing-entities__panel-sub">
                The Zoho customer is created automatically from these details.
              </div>

              {/* (a) Entity type — Individual is the default */}
              <div className="billing-entities__segmented">
                <button
                  type="button"
                  className={`billing-entities__segment ${createForm.entity_type !== 'business' ? 'billing-entities__segment--active' : ''}`}
                  onClick={() => setCreateForm((p) => ({ ...p, entity_type: 'individual' }))}
                >
                  Individual
                </button>
                <button
                  type="button"
                  className={`billing-entities__segment ${createForm.entity_type === 'business' ? 'billing-entities__segment--active' : ''}`}
                  onClick={() => setCreateForm((p) => ({ ...p, entity_type: 'business' }))}
                >
                  Business
                </button>
              </div>

              {/* (b) Name — business also carries an optional contact person
                  (2026-08-09): First/Last shown for BOTH types, mandatory
                  only for individuals. */}
              <div className="billing-entities__create-section">
                {createForm.entity_type === 'business' && (
                  <div className="billing-entities__field">
                    <label className="billing-entities__label">Business Name *</label>
                    <input
                      className="billing-entities__input"
                      type="text"
                      value={createForm.legal_name}
                      onChange={(e) => setCreateForm((p) => ({ ...p, legal_name: e.target.value }))}
                      placeholder="Registered company name"
                    />
                  </div>
                )}
                <div className="billing-entities__name-row">
                  <div className="billing-entities__field">
                    <label className="billing-entities__label">
                      {createForm.entity_type === 'business' ? 'Contact First Name' : 'First Name *'}
                    </label>
                    <input
                      className="billing-entities__input"
                      type="text"
                      value={createForm.first_name}
                      onChange={(e) => setCreateForm((p) => ({ ...p, first_name: e.target.value }))}
                      placeholder="e.g. Sarah"
                    />
                  </div>
                  <div className="billing-entities__field">
                    <label className="billing-entities__label">
                      {createForm.entity_type === 'business' ? 'Contact Last Name' : 'Last Name *'}
                    </label>
                    <input
                      className="billing-entities__input"
                      type="text"
                      value={createForm.last_name}
                      onChange={(e) => setCreateForm((p) => ({ ...p, last_name: e.target.value }))}
                      placeholder="e.g. Al Marri"
                    />
                  </div>
                </div>
              </div>

              {/* (c) Tax */}
              <div className="billing-entities__create-section">
                <div className="billing-entities__subheading">Tax</div>
                <div className="billing-entities__tax-row">
                  <label className="billing-entities__checkbox">
                    <input
                      type="checkbox"
                      checked={!!createForm.is_vat_registered}
                      onChange={(e) => setCreateForm((p) => ({ ...p, is_vat_registered: e.target.checked }))}
                    />
                    <span>VAT registered</span>
                  </label>
                  <div className="billing-entities__field billing-entities__field--grow">
                    <label className="billing-entities__label">TRN Number</label>
                    <input
                      className="billing-entities__input"
                      type="text"
                      value={createForm.trn_number}
                      onChange={(e) => setCreateForm((p) => ({ ...p, trn_number: e.target.value }))}
                      placeholder={createForm.is_vat_registered ? '15-digit TRN' : 'Tick VAT registered to enter a TRN'}
                      disabled={!createForm.is_vat_registered}
                    />
                  </div>
                </div>
              </div>

              {/* (d) Billing address */}
              <div className="billing-entities__create-section">
                <div className="billing-entities__subheading">Billing address</div>
                <div className="billing-entities__address-grid">
                  <div className="billing-entities__field billing-entities__field--span2">
                    <label className="billing-entities__label">Address Line 1</label>
                    <input
                      className="billing-entities__input"
                      type="text"
                      value={createForm.address_line_1}
                      onChange={(e) => setCreateForm((p) => ({ ...p, address_line_1: e.target.value }))}
                      placeholder="Office / Address"
                    />
                  </div>
                  <div className="billing-entities__field">
                    <label className="billing-entities__label">City</label>
                    <input
                      className="billing-entities__input"
                      type="text"
                      value={createForm.city}
                      onChange={(e) => setCreateForm((p) => ({ ...p, city: e.target.value }))}
                      placeholder="Dubai"
                    />
                  </div>
                  <div className="billing-entities__field">
                    <label className="billing-entities__label">State/Emirate</label>
                    <select
                      className="billing-entities__input"
                      value={createForm.state}
                      onChange={(e) => setCreateForm((p) => ({ ...p, state: e.target.value }))}
                    >
                      {STATE_OPTIONS.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                  <div className="billing-entities__field billing-entities__field--span2">
                    <label className="billing-entities__label">Country</label>
                    <select
                      className="billing-entities__input"
                      value={createForm.country}
                      onChange={(e) => setCreateForm((p) => ({ ...p, country: e.target.value }))}
                    >
                      <option value="United Arab Emirates">United Arab Emirates</option>
                    </select>
                  </div>
                </div>
              </div>

              {addError && <div className="billing-entities__error">{addError}</div>}
              <div className="billing-entities__panel-actions">
                <button
                  type="button"
                  className="billing-entities__btn billing-entities__btn--ghost"
                  onClick={() => setAddMode(null)}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="billing-entities__btn billing-entities__btn--primary"
                  onClick={handleCreateSubmit}
                  disabled={saving}
                >
                  {saving ? 'Creating...' : 'Create Entity'}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default BillingEntities;
